/* eslint-disable max-lines */
import {
  Component, OnInit, OnDestroy, Inject, ChangeDetectorRef, ChangeDetectionStrategy
} from '@angular/core';
import { Observable, zip, Subscription, combineLatest } from 'rxjs';
import { Router } from '@angular/router';
import { take, map, switchMap, tap, filter } from 'rxjs/operators';
import { isNullOrUndefined } from '@app/util/util';

import { ANIMATIONS, TIME_TO_SLIDE, CLOSE_TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { AppFacade } from '@app/app.facade';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { UpsellResponse, InclusionType, UpsellInclusions, UpsellOptions } from '@shared/flight-itinerary/entities/upsell-flights';
import { MobileSector, Sector } from '@app/entities/flight-results';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { VIEWPORT_RESIZE } from '@app/modules/core/tokens/app.tokens';
import { ViewportSizeInfo } from '@app/browser/app-base-browser.module';
import { AMPLITUDE_EVENTS, EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { environment as ENV } from '@environment';
import { flightAmplitudeProductPropertiesFromResultsMapper } from '@app/modules/core/mappers/flights-amplitude.mapper';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { UpsellFacade } from '@upsell/upsell.facade';
import { bookingBaseRoute } from '@booking/constants/booking-routes.constants';
import { EventProperties } from '@avo/interface/trigger-props';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { TriggerAmplitudeEntity } from '@amplitude/interfaces/trigger-entity';
import { StyleDots } from '@app/modules/carousel/entities/carousel-direction';
import { airPackagePropsFromUpsellResults } from '@amplitude/mappers/package-amplitude.mapper';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';
import { showUserTag } from '@ux/components/member-rate-tag/mappers/tag.mapper';
import { cabinTypeNameMapper } from '@flight-results/mappers/flights.mapper';
import { TagType } from '@ux/components/member-rate-tag/entities/tag-type';
import { TranslatePipe } from '@app/translate/TranslatePipe';
import { mapflightClassUnique } from '@upsell/mappers/upsell.mapper';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { ButtonIconSize } from '@design-system/components/molecules/button-icon/entities/button-icon.types';

@Component({
  selector: 'app-mobile-upsell',
  templateUrl: './mobile-upsell.component.html',
  styleUrls: ['./mobile-upsell.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MobileUpsellComponent implements OnInit, OnDestroy {

  public animation: string = ANIMATIONS.SLIDE_IN_BOTTOM;
  public fadeOut: boolean = false;
  public showActionBar: boolean = false;
  public eventSource: string = EVENT_SOURCE.RESULTS;
  public styleDots?: StyleDots = StyleDots.Upsell;

  public readonly buttonIconSize = ButtonIconSize.LG;

  public readonly icons = Icon;

  private isPackages: boolean;
  private optionId: number;
  private basePrice: number = 0;
  private flightOptions: Readonly<{ options: MobileSector[], provider: string }> = null;
  private subscriptions: Subscription = new Subscription();
  private packageHotelOptionId: number;
  private packageHotelSearchId: string;
  private packageHotelUpsell: HotelSearched;
  private passengersQuantity: number;

  constructor(
    private router: Router,
    private domManipulateService: DomManipulateService,
    private appFacade: AppFacade,
    private upsellFacade: UpsellFacade,
    private changeDetector: ChangeDetectorRef,
    private amplitude: PropertiesAmplitudeService,
    private triggerAmplitudeService: TriggerAmplitudeService,
    private readonly translatePipe: TranslatePipe,
    @Inject(VIEWPORT_RESIZE) public viewPortResize: Observable<ViewportSizeInfo>
  ) {
    if (this.router.getCurrentNavigation()?.extras && this.router.getCurrentNavigation()?.extras?.state) {
      this.isPackages = !!this.router.getCurrentNavigation().extras.state.isPackages;
      this.optionId = this.router.getCurrentNavigation().extras.state.OptionId;
      this.eventSource = this.router.getCurrentNavigation().extras.state.EventSource;
      this.basePrice = this.router.getCurrentNavigation().extras.state.BasePrice;
      this.flightOptions = this.router.getCurrentNavigation().extras.state.FlightOptions;
      this.packageHotelOptionId = this.router.getCurrentNavigation().extras.state.packageHotelOptionId;
      this.packageHotelSearchId = this.router.getCurrentNavigation().extras.state.packageHotelSearchId;
      this.packageHotelUpsell = this.router.getCurrentNavigation().extras.state.packageHotelUpsell;
      this.passengersQuantity = this.router.getCurrentNavigation().extras.state.passengersQuantity;
    }
  }

  ngOnInit(): void {
    this.domManipulateService.setBodyScroll(false);
    this.subscriptions.add(
      this.viewPortResize.subscribe(info => {
        this.showActionBar = info.actionBarVisible;
        this.changeDetector.detectChanges();
      })
    );
    this.registerViewUpsellOptionsAmplitudeEvent();
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
    this.subscriptions.unsubscribe();
    this.upsellFacade.resetUpsellAction();
  }

  get isPrime$(): Observable<boolean> {
    return this.appFacade.isPrime$;
  } 

  get hasRappiCard$(): Observable<boolean> {
    return this.appFacade.hasRappiCard$;
  } 

  get tagType$(): Observable<TagType> {
    return combineLatest([this.isPrime$, this.hasRappiCard$])
      .pipe(
        map(([isPrime, hasRappiCard]) => showUserTag(hasRappiCard, isPrime))
      )
  }

  get airlineCode(): string {
    return this.flightOptions?.options?.[0].Flights?.[0].AirlineCode;
  }

  get isLoadingFligthsUpsell$(): Observable<boolean> {
    return this.upsellFacade.isLoadingUpsell$;
  }

  get fligthsUpsell$(): Observable<UpsellResponse> {
    return this.upsellFacade.upsell$;
  }

  private get simpleFlightOptions(): Readonly<{ options: Sector[], provider: string }> {
    return { options: this.flightOptions?.options as Sector[], provider: this.flightOptions?.provider };
  }

  public flightClassText(cabin: number): string {
    return !isNullOrUndefined(cabin) ? 
      this.translatePipe.transform(`FLIGHT_RESULTS_MODULE.FLIGHT_CLASSES.${this.labelFlightCabin(cabin.toString())}`): '';
  }

  public getflightClassUnique(cabins: number[]): number[] {
    return mapflightClassUnique(cabins);
  }

  public labelFlightCabin(cabin: string): string {
    return cabinTypeNameMapper(cabin);
  }

  public justOneOption(upsellResponse: UpsellResponse): boolean {
    return upsellResponse.UpsellOptions.length === 1;
  }

  public calcUpsellItemValues(fare: number, taxes: number): number {
    return fare + taxes
  }

  public selectUpsell(upsellOption: UpsellOptions, index: number): void {
    if (!this.isPackages) this.selectFlightUpsell(upsellOption, index);
    else this.selectPackageUpsell(upsellOption, index);
  }

  public close(inmediately: boolean = false): void {
    this.animation = ANIMATIONS.SLIDE_OUT_BOTTOM;
    this.fadeOut = true;
    setTimeout(() => {
      this.appFacade.navigateToLastRoute();
    }, inmediately ? 0 : TIME_TO_SLIDE);
  }

  public rowLabel(inclusion: UpsellInclusions): string {
    return inclusion.Title ? `${inclusion.RowName} ${inclusion.Title}` : inclusion.RowName;
  }

  private registerViewUpsellOptionsAmplitudeEvent(): void {
    this.subscriptions.add(
      combineLatest([this.isLoadingFligthsUpsell$, this.fligthsUpsell$]).pipe(
        filter(([isLoadingFligthsUpsell]) => !isLoadingFligthsUpsell),
        map(([isLoadingFligthsUpsell, fligthsUpsell]) => ({ isLoadingFligthsUpsell, fligthsUpsell })),
        tap(({ fligthsUpsell }) => {
          if (!fligthsUpsell?.UpsellOptions) {
            this.close(true);
          }
        })
      ).subscribe()
    );
  }

  private selectPackageUpsell(upsellOption: UpsellOptions, index: number): void {
    let upsellId = '';
    zip(
      this.fligthsUpsell$, this.upsellFacade.searchPayload$.pipe(take(1)),
      this.amplitude.searchProperties$(SearchBy.PACKAGES).pipe(take(1)),
    ).pipe(
      map(([flightsUpsell, searchPayload, searchProps]) => ({
        flightsUpsell, searchPayload, searchProps,
        options: this.simpleFlightOptions.options,
        provider: this.simpleFlightOptions.provider
      })),
      tap(({ flightsUpsell }) => upsellId = flightsUpsell.UpsellId),
      map(({ options, provider, searchPayload, searchProps }) => ({
        searchProps,
        addToCartEventProps: this.getAddToCardProps(index, this.basePrice),
        eventAddToCartProductProps: airPackagePropsFromUpsellResults(
          true, this.packageHotelUpsell, options, provider, this.basePrice
        ),
        navigateCommands: [
          bookingBaseRoute, searchPayload.searchId, this.optionId,
          (this.packageHotelSearchId || searchPayload.searchId),
          (this.packageHotelOptionId || searchPayload.packageHotelOptionId),
          searchPayload.packageHotelRateId, TravelBookingType.PACKAGE_AIR, ENV.countryInfo.currency_code,
          ENV.countryInfo.iso_code, upsellId, upsellOption.OptionId
        ]
      })),
      switchMap(({ addToCartEventProps, searchProps, eventAddToCartProductProps, navigateCommands }) => zip(
        this.triggerAmplitudeService.trackAmplitudeEventProps$(
          AMPLITUDE_EVENTS.ADD_TO_CART, addToCartEventProps, searchProps, eventAddToCartProductProps
        ).pipe(take(1)),
      ).pipe(
        tap(([triggerAddToCard]) => {
          this.trackEventAndRedirect(navigateCommands, triggerAddToCard);
        })
      ))
    ).subscribe().unsubscribe();
  }

  private selectFlightUpsell(upsellOption: UpsellOptions, index: number): void {
    let upsellId = '';
    zip(
      this.fligthsUpsell$, this.upsellFacade.searchPayload$.pipe(take(1)),
      this.amplitude.searchProperties$(SearchBy.FLIGHTS)
    ).pipe(
      map(([flightsUpsell, searchPayload, searchProps]) => ({
        flightsUpsell, searchPayload, searchProps
      })),
      tap(({ flightsUpsell }) => upsellId = flightsUpsell.UpsellId),
      map(({ searchPayload, searchProps }) => ({
        searchProps,
        addToCartEventProps: this.getAddToCardProps(index, this.basePrice),
        productProps: flightAmplitudeProductPropertiesFromResultsMapper(this.flightOptions?.options,
          this.flightOptions?.provider, this.basePrice, upsellOption.HasPrivateFare),
        navigateCommands: [
          bookingBaseRoute, searchPayload.searchId, this.optionId, 'null', TravelBookingType.FLIGHTS,
          upsellId, upsellOption.OptionId
        ]
      })),
      switchMap(({ addToCartEventProps, searchProps, productProps, navigateCommands }) => zip(
        this.triggerAmplitudeService.trackAmplitudeEventProps$(
          AMPLITUDE_EVENTS.ADD_TO_CART, addToCartEventProps, searchProps, productProps
        ).pipe(take(1)),
      ).pipe(
        tap(([triggerAddToCard ]) => {
          this.trackEventAndRedirect(navigateCommands, triggerAddToCard);
        })
      ))
    ).subscribe().unsubscribe();
  }

  private getUpsellPrice(upsellOption: UpsellOptions): number {
    return upsellOption.Amount.Fare + upsellOption.Amount.Taxes;
  }

  private getAddToCardProps(index: number, basePrice: number): EventProperties {
    return {
      INDEX: index + 1,
      BASE_PRICE: basePrice
    };
  }

  private trackEventAndRedirect(navigateCommands: (string | number)[],
    triggerAddToCart: TriggerAmplitudeEntity): void {
    triggerAmplitudeEventApp(triggerAddToCart);
    this.close();
    setTimeout(() => this.router.navigate(navigateCommands, { skipLocationChange: false }), CLOSE_TIME_TO_SLIDE);
  }

  public filterIncludedOptions(option: UpsellOptions): UpsellInclusions[] {
    return this.filterInclusionsByType(option?.Families[0]?.Inclusions, InclusionType.Included);
  }

  public filterNotOfferedOptions(option: UpsellOptions): UpsellInclusions[] {
    return this.filterInclusionsByType(option?.Families[0]?.Inclusions, InclusionType.NotOffered);
  }

  public filterAtChargeOptions(option: UpsellOptions): UpsellInclusions[] {
    return this.filterInclusionsByType(option?.Families[0]?.Inclusions, InclusionType.AtCharge);
  }

  private filterInclusionsByType(inclusions: UpsellInclusions[], inclusionTypeFilter: InclusionType): UpsellInclusions[] {
    return inclusions.filter(({ Type }) => Type === inclusionTypeFilter);
  }

  public showBorderForNotOffered(option: UpsellOptions): boolean {
    return this.hasIncludedOptions(option) && this.hasNotOfferedOptions(option);
  }

  public showBorderForAtCharge(option: UpsellOptions): boolean {
    return this.hasIncludedOptions(option) || this.hasNotOfferedOptions(option);
  }

  private hasIncludedOptions(option: UpsellOptions): boolean {
    return this.filterIncludedOptions(option).length > 0;
  }

  private hasNotOfferedOptions(option: UpsellOptions): boolean {
    return this.filterNotOfferedOptions(option).length > 0;
  }
}
